import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ImportsApi } from '@/services/api-imports';
import { teamPath } from '@/utils/paths';

export const useImportsStore = defineStore('imports', () => {
  // STATE
  const STATUS = {
    READY: 'Ready',
    PROCESSING: 'Processing',
    PROCESSED: 'Processed',
    FAILED: 'Failed',
  };

  const imports = ref([]);

  // Track when a new import is added to the list
  const newImportId = ref(null);
  let newImportTimer = null;

  // Append more imports
  const hasMoreImports = ref(false);
  const nextCursor = ref(null);
  const isLoadingMore = ref(false);

  //GETTERS
  const sourceFileUrl = computed(() => {
    return (id) => (id ? `${teamPath()}/imports/${id}/download` : '');
  });

  const getImportById = computed(() => {
    return (id) => (id ? imports.value.find((item) => item.id === id) : {});
  });

  /**
   * Clears pagination state (no more imports available)
   * @private
   */
  function clearPaginationState() {
    hasMoreImports.value = false;
    nextCursor.value = null;
  }

  /**
   * Updates pagination state based on API response
   * @param {Object} response - API response object with links and meta
   * @private
   */
  function updatePaginationState(response) {
    if (!response.meta?.total || imports.value.length >= response.meta.total) {
      clearPaginationState();
      return;
    }

    hasMoreImports.value = true;
    // Extract the cursor from the next URL (e.g., "/?next=2775148400824079483")
    const nextUrl = new URL(response.links.next, window.location.origin);
    nextCursor.value = nextUrl.searchParams.get('next');
  }

  /**
   * Builds the filter object for API requests
   * @param {boolean} append - Whether this is for pagination
   * @returns {Object} Filter object for API request
   * @private
   */
  function buildImportsFilter(append) {
    const filter = {
      status: `${STATUS.PROCESSING},${STATUS.PROCESSED}`,
      sort: 'created:desc',
      limit: 20,
    };

    if (append && nextCursor.value) {
      filter.next = nextCursor.value;
    }

    return filter;
  }

  /**
   * Handles the API response and updates the imports array
   * @param {Object} response - API response
   * @param {boolean} append - Whether to append or replace imports
   * @private
   */
  function handleImportsResponse(response, append) {
    const newImports = response.data || response;

    if (append) {
      // Append new imports to existing list, avoiding duplicates
      const existingIds = new Set(imports.value.map((item) => item.id));
      const uniqueNewImports = newImports.filter((item) => !existingIds.has(item.id));
      imports.value.push(...uniqueNewImports);
    } else {
      // Replace imports for initial load
      imports.value = newImports;
    }

    updatePaginationState(response);
  }

  /**
   * Handles fetch errors and resets state if needed
   * @param {Error} error - The error that occurred
   * @param {boolean} append - Whether this was an append operation
   * @private
   */
  function handleImportsError(error, append) {
    console.error(`Failed to ${append ? 'load more' : 'fetch'} imports:`, error);

    if (!append) {
      imports.value = [];
      clearPaginationState();
    }
  }

  /**
   * Clears the new import timer and resets the newImportId
   * @private
   */
  function clearNewImportTimer() {
    newImportId.value = null;
    clearTimeout(newImportTimer);
    newImportTimer = null;
  }

  /**
   * Fetches imports with processing and processed status
   * @param {boolean} append - Whether to append to existing imports (for pagination) or replace them
   * @returns {Promise} Promise that resolves when imports are fetched
   * @throws {Error} Throws error if API request fails
   */
  async function fetchImports(append = false) {
    // Prevent multiple concurrent load more requests
    if (append && (!hasMoreImports.value || !nextCursor.value || isLoadingMore.value)) {
      return;
    }

    if (append) {
      isLoadingMore.value = true;
    }

    try {
      const filter = buildImportsFilter(append);
      const response = await ImportsApi.index(filter);

      if (!response) {
        if (!append) {
          imports.value = [];
          clearPaginationState();
        }
        return;
      }

      handleImportsResponse(response, append);
    } catch (error) {
      handleImportsError(error, append);
      throw error;
    } finally {
      if (append) {
        isLoadingMore.value = false;
      }
    }
  }

  /**
   * Fetches a single import by ID and updates the store
   * @param {string} id - The import ID to fetch
   * @returns {Promise|undefined} Promise that resolves when import is fetched, or undefined if no ID provided
   * @throws {Error} Throws error if API request fails
   */
  async function fetchSingleImport(id) {
    if (!id) {
      return;
    }

    try {
      const index = imports.value.findIndex((item) => item.id === id);
      const response = await ImportsApi.show(id);

      if (index === -1) {
        imports.value.unshift(response);
        return;
      }

      imports.value[index] = response;
    } catch (error) {
      console.error(`Failed to fetch import ${id}:`, error);
      throw error;
    }
  }

  /**
   * Start a timer that clears the newImportId after 5 seconds
   * Useful for visual styling of newly added imports
   * @param {string} id - The import ID to set the timer on'
   * @param {number} [ms=5000] - Timeout milliseconds
   */
  function startNewImportTimer(id, ms = 5000) {
    if (!id) {
      return;
    }

    clearTimeout(newImportTimer);

    newImportId.value = id;
    newImportTimer = setTimeout(() => {
      newImportId.value = null;
      newImportTimer = null;
    }, ms);
  }

  /**
   * Resets the store state back to initial values
   */
  function $reset() {
    imports.value = [];
    isLoadingMore.value = false;
    clearPaginationState();
    clearNewImportTimer();
  }

  return {
    // State
    STATUS,
    imports,
    newImportId,
    hasMoreImports,
    nextCursor,
    isLoadingMore,
    // Getters
    sourceFileUrl,
    getImportById,
    // Actions
    fetchImports,
    fetchSingleImport,
    startNewImportTimer,
    $reset,
  };
});
